import CloseIcon from "@mui/icons-material/Close";
import Sortable from "sortablejs";
import KanbanColumn from "../../components/CAR&PAR/CARComponents/KanbanColumn";
import CARCreateDialog from "../../components/CAR&PAR/CARComponents/CARCreateDialog";
import CARDetailView from "../../components/CAR&PAR/CARComponents/CARDetailView";
import { getCARRecords } from "../../api/GETCAR";
import { updateCARRecord } from "../../utils/updateCARRecord";
import { useEffect, useState, useRef } from "react";
//import UpdateCAR from "./CARComponents/UpdateCAR";
//import DummyKanbanCard from "../../components/CAR&PAR/CARComponents/DummyKanbanCard";
import { updateCARStatus } from "../../utils/updateCARStatus";
// Example parent component implementation
import { toast } from "react-toastify";
import KanbanCard from "../../components/CAR&PAR/CARComponents/KanbanCard";
import {
  Box,
  Typography,
  Button,
  Grow,
  Collapse,
  IconButton,
  Alert,
  Snackbar,
} from "@mui/material";
import Loader from "../../components/common/Loader";
import Swal from "sweetalert2";
import PluseIconDesign from "../common/PluseIconDesign";

// Map column IDs to actual status values for the API
const columnToStatusMap = {
  open: "Total CAR Open",
  closed: "Total CAR Closed",
  pastdue: "Total CAR Past-Due",
};

const columns = [
  { id: "open", title: "Total CAR Open", color: "#e0f7fa" },
  { id: "closed", title: "Total CAR Closed", color: "#e8f5e9" },
  { id: "pastdue", title: "Total CAR Past-Due", color: "#fff3e0" },
];

export default function MainCAR() {
  const [cards, setCards] = useState({
    open: [],
    closed: [],
    pastdue: [],
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const sortableInstances = useRef({});
  const isMounted = useRef(true);

  //const [activeColumn, setActiveColumn] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  console.log("Detail open state:", detailOpen);

  // Notification helper
  const showNotification = (message, severity = "info") => {
    if (isMounted.current) {
      setNotification({
        open: true,
        message,
        severity,
      });
    }
  };

  const mapStatusToColumnId = (status) => {
    if (!status) return "open";
    const normalized = status.toLowerCase();
    if (normalized.includes("open")) return "open";
    if (normalized.includes("closed")) return "closed";
    if (normalized.includes("past")) return "pastdue";
    return "open"; // fallback default
  };

  const groupByStatus = (records) => {
    return records.reduce(
      (acc, record) => {
        const columnId = mapStatusToColumnId(record.status);
        if (!acc[columnId]) acc[columnId] = [];
        acc[columnId].push(record);
        return acc;
      },
      {
        open: [],
        closed: [],
        pastdue: [],
      }
    );
  };

  const fetchCARData = async () => {
    if (!isMounted.current) return;

    setIsLoading(true);
    try {
      const records = await getCARRecords();
      console.log("✅ Raw Records from Zoho:", records);

      if (!records || !Array.isArray(records) || records.length === 0) {
        showNotification("No CAR records found", "warning");
        setCards({
          open: [],
          closed: [],
          pastdue: [],
        });
      } else {
        const grouped = groupByStatus(records);
        setCards(grouped);
      }
    } catch (error) {
      console.error("🚨 Failed to load CARs:", error);
      showNotification("Failed to load CAR records", "error");
      setCards({
        open: [],
        closed: [],
        pastdue: [],
      });
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  };

  // Function to update card status
  const updateCardStatus = async (cardId, toCol) => {
    if (!cardId || !toCol || isUpdating) return false;

    setIsUpdating(true);
    try {
      const newStatus = columnToStatusMap[toCol];
      console.log(`Updating card ${cardId} to status ${newStatus}`);

      const result = await updateCARRecord(cardId, {
        Status_Kanban: newStatus,
      });

      if (result) {
        showNotification("Card status updated successfully", "success");
        await fetchCARData(); // Refresh the data to ensure consistent state
        return true;
      } else {
        showNotification("Failed to update card status", "error");
        return false;
      }
    } catch (error) {
      console.error("Failed to update card status:", error);
      showNotification("Error updating card status", "error");
      return false;
    } finally {
      if (isMounted.current) {
        setIsUpdating(false);
      }
    }
  };

  // Initialize fetch
  useEffect(() => {
    fetchCARData();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleCardClick = (columnId, card) => {
    console.log("Card clicked:", card);
    setSelectedCard(card);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Clean up Sortable instances safely
  const cleanupSortable = () => {
    Object.keys(sortableInstances.current).forEach((key) => {
      try {
        if (sortableInstances.current[key]) {
          sortableInstances.current[key].destroy();
          sortableInstances.current[key] = null;
        }
      } catch (error) {
        console.error(`Error destroying sortable instance ${key}:`, error);
      }
    });
  };

  // Setup sortable with improved error handling
  useEffect(() => {
    if (isLoading || isUpdating) return;

    // Clean up previous instances first
    cleanupSortable();

    // Initialize new instances
    columns.forEach((col) => {
      const el = document.getElementById(col.id);
      if (!el) return;

      try {
        const options = {
          group: "car-kanban",
          animation: 150,
          ghostClass: "sortable-ghost",
          disabled: isUpdating,

          // Safe event handlers
          onStart: function (evt) {
            console.log(`Drag started in ${col.id}`);
          },

          onEnd: async function (evt) {
            const fromCol = evt.from.id;
            const toCol = evt.to.id;

            if (fromCol === toCol) return;

            try {
              const cardElement = evt.item;
              const cardId = cardElement?.getAttribute("data-id");

              if (!cardId) {
                console.error("No card ID found on dragged element");
                return;
              }

              console.log(`Moving card ${cardId} from ${fromCol} to ${toCol}`);

              // any React updates might remove it from the DOM
              const sourceParent = cardElement.parentNode;

              // Update the status
              const success = await updateCardStatus(cardId, toCol);

              // If update failed and element still exists, try to revert DOM
              if (!success && sourceParent && cardElement.parentNode) {
                try {
                  console.log("Update failed, attempting to revert DOM change");
                  if (evt.from && evt.from.contains(cardElement)) {
                    // Already in the right place
                  } else if (sourceParent && evt.from) {
                    sourceParent.appendChild(cardElement);
                  }
                } catch (err) {
                  console.error("Failed to manually revert DOM:", err);
                  // If we can't fix the DOM, force a full refresh
                  fetchCARData();
                }
              }
            } catch (error) {
              console.error("Error in onEnd handler:", error);
              fetchCARData(); // Refresh on error
            }
          },
        };

        sortableInstances.current[col.id] = Sortable.create(el, options);
      } catch (error) {
        console.error(`Error creating Sortable for column ${col.id}:`, error);
      }
    });

    return () => {
      cleanupSortable();
    };
  }, [cards, isLoading, isUpdating]);

  if (isLoading) {
    return <Loader message="" />;
  }

  // Handler for 3-dots actions
  // Updated handleCardAction with improved error handling
  const handleCardAction = async (cardId, card, action) => {
    console.log("MainCAR handleCardAction called with:", {
      cardId,
      card,
      action,
    });

    if (!cardId || !card) {
      console.error("Card or card ID is missing");
      toast.error("Operation failed: Card information is incomplete.");
      return false;
    }

    setIsUpdating(true);
    try {
      // Call updateCARStatus to update the card status
      const updatedCard = await updateCARStatus(cardId, card, action);

      if (updatedCard) {
        // Map the action to the actual status for UI update
        let statusToSet = "";
        if (action === "Closed") {
          statusToSet = "Total CAR Closed";
        } else if (action === "PastDue") {
          statusToSet = "Total CAR Past-Due";
        } else {
          statusToSet = action;
        }

        // Update local state first for immediate UI feedback
        // Find which column the card is currently in
        let columnUpdated = false;
        const updatedCards = { ...cards };

        // Look in all columns
        for (const columnId in cards) {
          const cardIndex = cards[columnId].findIndex((c) => c.id === cardId);
          if (cardIndex !== -1) {
            // Remove from current column
            const updatedColumnCards = [...cards[columnId]];
            updatedColumnCards.splice(cardIndex, 1);
            updatedCards[columnId] = updatedColumnCards;

            // Add to target column based on status
            const targetColumn = mapStatusToColumnId(statusToSet);
            updatedCards[targetColumn] = [
              ...updatedCards[targetColumn],
              { ...card, status: statusToSet, Status_Kanban: statusToSet },
            ];

            columnUpdated = true;
            break;
          }
        }

        if (columnUpdated) {
          setCards(updatedCards);
        }

        // Show success notification
        toast.success(`Successfully moved to ${action}!`);

        // Refresh data to ensure consistency
        await fetchCARData();
        return true;
      } else {
        toast.error("Status update failed. Please try again.");
        return false;
      }
    } catch (error) {
      console.error("Error in handleCardAction:", error);
      toast.error("Operation failed. Please try again.");
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Box sx={{ p: 2, minHeight: "100vh" }}>
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6" fontWeight="bold" sx={{ color: "#4F46E5" }}>
          Corrective Action Request
        </Typography>
        <PluseIconDesign
          variant="contained"
          sx={{
            backgroundColor: "#4F46E5",
            "&:hover": { backgroundColor: "#4338CA" },
          }}
          onClick={() => setCreateDialogOpen(true)}
        />
      </Box>

      {/* Kanban columns with explicit console logging */}
      <Grow in timeout={500}>
        <Box display="flex" gap={2} sx={{ overflowX: "auto" }}>
          {columns.map((col) => {
            console.log("Passing to KanbanColumn:", {
              handleCardAction,
              updateCardStatus,
            });

            return (
              <KanbanColumn
                key={col.id}
                column={col}
                cards={cards[col.id] || []}
                selectedCard={selectedCard}
                onCardClick={handleCardClick}
                onCardAction={handleCardAction}
                updateCardStatus={(cardId, status) =>
                  updateCardStatus(cardId, mapStatusToColumnId(status))
                }
              />
            );
          })}
        </Box>
      </Grow>

      {/* Only one CARDetailView, not recursive */}
      <CARDetailView
        open={detailOpen}
        card={selectedCard}
        onClose={handleDetailClose}
        fetchCARData={fetchCARData} // Pass fetchCARData to CARDetailView
      />

      <CARCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        formData={formData}
        handleInputChange={handleInputChange}
        fetchCARData={fetchCARData}
      />

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
